import { DetailBreadcrumb } from "./DetailBreadcrumb";
import { Link } from "wouter";
import { useEffect, useState, type ReactNode } from "react";
import { Bookmark, Check, CheckCircle2, ChevronRight, FileText, Lock, MessageCircle, ShieldCheck, Star } from "lucide-react";
import { ImageCarousel } from "./ImageCarousel";
import { LocationPin } from "./LocationPin";
import { MobileActionBar } from "./MobileActionBar";
import { SaveButton } from "./SaveButton";
import { AccountVerificationBadge } from "./AccountVerificationBadge";
import { useInPageActionVisibility } from "../lib/useInPageActionVisibility";
import { api, type PostingIdentity } from "../lib/api";
import { peso } from "../data/mock";
import { useI18n } from "../lib/i18n";

type ProjectSummary = {
  postingIdentity?: PostingIdentity;
  id: string;
  title: string;
  category: string;
  location: string;
  budget: number;
  budgetType: string;
  deadline: string;
  targetCompletionDate: string | null;
  bids: number;
  posted: string;
  description: string;
  scope: string[];
  requirements: string[];
  licenses: string[];
  ownerUserId: number | null;
  clientSummary: {
    name: string;
    profileImageUrl: string | null;
    memberSince: string;
    projectsPosted: number;
    projectsAwarded: number;
    rating: number | null;
    reviewCount: number;
    identityVerified: boolean;
    verifiedProfessions?: string[];
  } | null;
};

type ProjectBidDocument = { id: string; filename: string; size: number; contentType: string; createdAt: string };
type ProjectBid = {
  id: string;
  price: number;
  standingPrice?: number | null;
  status: string;
  durationText?: string | null;
  terms?: string | null;
  documents?: ProjectBidDocument[];
};
type ProjectAttachment = { id: string; filename: string; size: number };
type ProjectMessage = { id: string; senderUserId: number; senderName: string; message: string; createdAt: string };

type ProjectBiddingDetailMobileProps = {
  project: ProjectSummary;
  signedIn: boolean;
  currentUserId?: number;
  canEditBid: boolean;
  biddingClosed: boolean;
  awarded: boolean;
  myBid: ProjectBid | null;
  attachments: ProjectAttachment[];
  questions: ProjectMessage[];
  questionForm: ReactNode;
  questionClosedNotice?: ReactNode;
  error: string | null;
  onOpenBid: () => void;
  onSignIn: () => void;
  onMessage: () => void;
  canMessage: boolean;
  onViewAllFiles: () => void;
  onDownloadBidDocument: (bidId: string, document: ProjectBidDocument) => void;
};

const INITIAL_SCOPE_COUNT = 3;
const INITIAL_ATTACHMENT_COUNT = 2;

function bidState(status: string, t: (key: string) => string): string {
  if (status === "awarded") return t("activity.status.awarded");
  if (status === "shortlisted" || status === "in_negotiation") return t("activity.status.shortlisted");
  if (status === "rejected") return t("bid.notSelected");
  return t("bid.submitted");
}

function fileSize(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export function ProjectBiddingDetailMobile({
  project, signedIn, currentUserId, canEditBid, biddingClosed, awarded, myBid, attachments, questions,
  questionForm, questionClosedNotice, error, onOpenBid, onSignIn, onMessage, canMessage, onViewAllFiles,
  onDownloadBidDocument,
}: ProjectBiddingDetailMobileProps) {
  const { t } = useI18n();
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [showAllScope, setShowAllScope] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const { actionRef, isVisible: actionIsVisible } = useInPageActionVisibility<HTMLDivElement>(project.id, true);

  useEffect(() => setAvatarFailed(false), [project.clientSummary?.profileImageUrl, project.postingIdentity?.imageUrl]);

  const canStartBid = !awarded && (!biddingClosed || canEditBid);
  const bidAction = canEditBid
    ? <button type="button" onClick={onOpenBid} className="btn-red min-w-0 flex-1"><Lock className="h-4 w-4" />{myBid ? "Revise your Bid" : t("activity.submitBid")}</button>
    : !signedIn && canStartBid
      ? <button type="button" onClick={onSignIn} className="btn-red min-w-0 flex-1">Sign in to bid</button>
      : null;
  const business = project.postingIdentity?.kind === "business" ? project.postingIdentity : null;
  const clientImage = business ? business.imageUrl : project.clientSummary?.profileImageUrl;
  const clientName = business?.name || project.clientSummary?.name || "Project client";
  const initials = clientName.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "P";
  const visibleAttachments = attachments.slice(0, INITIAL_ATTACHMENT_COUNT);
  const visibleScope = showAllScope ? project.scope : project.scope.slice(0, INITIAL_SCOPE_COUNT);

  return <div className="bg-[#f3f6f9] pb-24 text-navy sm:hidden">
    <DetailBreadcrumb title={project.title} fallback={{ href: "/marketplace?tab=Project%20Bidding", label: "Project Bidding" }} className="px-4 py-3 text-xs" />

    <div className="relative">
      <ImageCarousel type="project" id={project.id} compact narrowThumbnails mobileService />
      {signedIn
        ? <SaveButton kind="project" refId={project.id} imageOverlay iconClassName="h-6 w-6" className="absolute right-4 top-3 z-20 h-10 w-10" />
        : <button type="button" onClick={onSignIn} aria-label="Sign in to save project" className="absolute right-4 top-3 z-20 text-white"><Bookmark className="h-6 w-6 fill-white" /></button>}
    </div>

    <section className="bg-white px-4 pb-5 pt-5">
      <h1 className="text-[25px] font-extrabold leading-tight tracking-tight text-navy">{project.title}</h1>
      <div className="mt-4 flex min-w-0 items-center gap-2.5">
        <div className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-xl bg-[#dce9f0] text-sm font-extrabold text-[#1e4c6a]">
          {clientImage && !avatarFailed
            ? <img src={api.apiAssetUrl(clientImage) || undefined} alt="" className="h-full w-full object-cover" onError={() => setAvatarFailed(true)} />
            : initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-1.5 text-sm font-extrabold text-navy">{business?.href ? <Link href={business.href} className="min-w-0 truncate" title={clientName}>{clientName}</Link> : <span className="min-w-0 truncate" title={clientName}>{clientName}</span>}{!business && <AccountVerificationBadge identityVerified={project.clientSummary?.identityVerified} verifiedProfessions={project.clientSummary?.verifiedProfessions} />}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-slate-500">
            {!business && project.clientSummary?.rating != null && project.clientSummary.reviewCount > 0 && <span className="inline-flex items-center gap-1"><Star className="h-3.5 w-3.5 fill-brand-gold text-brand-gold" aria-hidden="true" />{project.clientSummary.rating.toFixed(1)} ({project.clientSummary.reviewCount})</span>}
            <span className="inline-flex items-center gap-1"><LocationPin className="h-3.5 w-3.5" />{project.location}</span>
          </div>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-1.5">
        <span className={`rounded-full px-2.5 py-1.5 text-[11px] font-bold ${awarded ? "bg-amber-50 text-amber-800" : biddingClosed ? "bg-blue-50 text-blue-700" : "bg-[#e5f5ea] text-[#167a4b]"}`}>{awarded ? t("activity.status.awarded") : biddingClosed ? "Shortlisting" : "Open for bidding"}</span>
        <span className="rounded-full bg-[#ebf2f7] px-2.5 py-1.5 text-[11px] font-bold text-[#305676]">{project.category}</span>
        <span className="rounded-full bg-[#ebf2f7] px-2.5 py-1.5 text-[11px] font-bold text-[#305676]">{project.location}</span>
      </div>
      <div className="mt-5 border-t border-slate-200 pt-4">
        <span className="block text-xs text-slate-500">{!project.budgetType || project.budgetType === "Estimated" ? t("detail.project.estimatedBudget") : `${project.budgetType} ${t("cards.budget")}`}</span>
        <strong className="mt-0.5 block text-2xl font-extrabold leading-tight text-navy">{project.budget > 0 ? peso(project.budget) : "Not provided"}</strong>
        <span className="mt-1 block text-xs text-slate-500">{project.bids} {t("bid.sealedBids")}{project.posted ? ` · ${t("bid.posted")} ${project.posted}` : ""}</span>
      </div>
      {(bidAction || canMessage) && <div ref={actionRef} className="mt-4 flex items-stretch gap-2">
        {bidAction}
        {canMessage && <button type="button" onClick={onMessage} aria-label="Message project client" title="Message project client" className="btn-outline w-11 shrink-0 p-0"><MessageCircle className="h-5 w-5" aria-hidden="true" /></button>}
      </div>}
      {canEditBid && !actionIsVisible && <span className="sr-only">Bid action is available in the sticky action bar.</span>}
      {bidStatusNotice(awarded, biddingClosed, canEditBid)}
      {error && <p role="alert" className="mt-3 text-xs text-brand-red">{error}</p>}
      <p className="mt-4 flex items-center gap-2 text-xs text-slate-500"><ShieldCheck className="h-4 w-4 shrink-0 text-brand-green" />{t("bid.sealedNotice")}</p>
    </section>

    {myBid && <section className="mt-2 bg-white px-4 py-6">
      <div className="flex items-center justify-between gap-2"><h2 className="text-lg font-extrabold text-navy">{t("bid.yourSealedBid")}</h2><span className="rounded-full bg-green-50 px-2.5 py-1 text-[11px] font-bold text-green-800">{bidState(myBid.status, t)}</span></div>
      <div className="mt-3 rounded-xl border border-green-200 bg-green-50/50 p-4 text-center">
        <CheckCircle2 className="mx-auto mb-1 h-7 w-7 text-brand-green" aria-hidden="true" />
        <strong className="block text-2xl font-extrabold text-navy">{peso(myBid.standingPrice ?? myBid.price)}</strong>
        {myBid.durationText && <span className="mt-1 block text-sm text-slate-600">{myBid.durationText}</span>}
      </div>
      {myBid.terms && <div className="mt-3 text-sm text-slate-600"><strong className="text-navy">{t("bid.termsExclusions")}</strong><p className="mt-1 whitespace-pre-wrap leading-6">{myBid.terms}</p></div>}
      {myBid.documents?.length ? <div className="mt-3"><strong className="text-sm text-navy">{t("bid.documents")}</strong><ul className="mt-1 divide-y divide-slate-200">{myBid.documents.map((document) => <li key={document.id}><button type="button" onClick={() => onDownloadBidDocument(myBid.id, document)} className="flex w-full items-center gap-2 py-2 text-left text-sm text-blue-700"><FileText className="h-4 w-4 shrink-0" /><span className="min-w-0 flex-1 break-all">{document.filename}</span></button></li>)}</ul></div> : null}
      {canEditBid && <button type="button" onClick={onOpenBid} className="btn-outline mt-4 h-11 w-full rounded-xl">{t("bid.revise")}</button>}
      {biddingClosed && !canEditBid && !awarded && <p className="mt-3 text-xs leading-5 text-slate-500">Your bid is read-only while the client reviews shortlisted providers.</p>}
    </section>}

    <section className="mt-2 bg-white px-4 py-6">
      <h2 className="text-lg font-extrabold text-navy">{t("project.descriptionHeading")}</h2>
      <p className={`mt-4 whitespace-pre-line text-sm leading-6 text-[#49627a] ${descriptionExpanded ? "" : "line-clamp-4"}`}>{project.description}</p>
      {project.description.length > 240 && <button type="button" onClick={() => setDescriptionExpanded((expanded) => !expanded)} aria-expanded={descriptionExpanded} className="mt-3 block w-full text-right text-sm font-bold text-blue-700">{descriptionExpanded ? "Show less" : "View more"}</button>}
    </section>

    <section className="mt-2 bg-white px-4 py-6">
      <h2 className="text-lg font-extrabold text-navy">Project details</h2>
      <div className="mt-4 grid grid-cols-2 gap-2">
        {[{ label: "Bidding deadline", value: project.deadline }, ...(project.targetCompletionDate ? [{ label: "Client target completion", value: project.targetCompletionDate }] : []), { label: "Location", value: project.location }, { label: "Bids received", value: `${project.bids} sealed` }].map(({ label, value }) => <div key={label} className="min-w-0 rounded-xl border border-slate-200 bg-slate-50 p-3"><span className="block text-[11px] text-slate-500">{label}</span><strong className="mt-1 block break-words text-xs text-navy">{value}</strong></div>)}
      </div>
      {project.targetCompletionDate && <p className="mt-3 text-xs leading-5 text-slate-500">The target date is requested by the client. It is not an agreed contract date.</p>}
    </section>

    <section className="mt-2 bg-white px-4 py-6">
      <div className="flex items-center justify-between gap-2"><h2 className="text-lg font-extrabold text-navy">Attachments &amp; project files</h2><span className="text-xs text-slate-500">{attachments.length} files</span></div>
      {visibleAttachments.length > 0 ? <div className="mt-3 divide-y divide-slate-200">{visibleAttachments.map((attachment) => <a key={attachment.id} href={api.attachmentDownloadUrl(project.id, attachment.id)} download={attachment.filename} className="flex min-w-0 items-center gap-3 py-3 text-navy"><span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-[#e8f0f6] text-[#315f7e]"><FileText className="h-5 w-5" /></span><span className="min-w-0 flex-1"><strong className="block truncate text-xs">{attachment.filename}</strong><small className="text-[11px] text-slate-500">{fileSize(attachment.size)}</small></span><ChevronRight className="h-4 w-4 shrink-0 text-slate-400" /></a>)}</div> : <p className="mt-3 text-sm text-slate-500">No attachments have been added to this project.</p>}
      {attachments.length > INITIAL_ATTACHMENT_COUNT && <button type="button" onClick={onViewAllFiles} className="mt-2 block w-full text-right text-sm font-bold text-blue-700">View all files</button>}
    </section>

    <section className="mt-2 bg-white px-4 py-6">
      <h2 className="text-lg font-extrabold text-navy">Scope of work</h2>
      {visibleScope.length > 0 ? <ul className="mt-3 space-y-2.5">{visibleScope.map((scopeItem) => <li key={scopeItem} className="flex gap-2.5 text-sm leading-6 text-[#49627a]"><Check className="mt-1 h-4 w-4 shrink-0 text-brand-green" aria-hidden="true" />{scopeItem}</li>)}</ul> : <p className="mt-3 text-sm text-slate-500">No separate scope of work was published.</p>}
      {project.scope.length > INITIAL_SCOPE_COUNT && <button type="button" onClick={() => setShowAllScope((expanded) => !expanded)} aria-expanded={showAllScope} className="mt-3 block w-full text-right text-sm font-bold text-blue-700">{showAllScope ? "Show less" : "View all scope"}</button>}
    </section>

    <section className="mt-2 bg-white px-4 py-6">
      <h2 className="text-lg font-extrabold text-navy">Requirements</h2>
      {[...project.requirements, ...project.licenses].length > 0
        ? <ul className="mt-3 space-y-2.5">{[...project.requirements, ...project.licenses].map((requirement, index) => <li key={`${requirement}-${index}`} className="flex gap-2.5 text-sm leading-6 text-[#49627a]"><Check className="mt-1 h-4 w-4 shrink-0 text-brand-green" aria-hidden="true" />{requirement}</li>)}</ul>
        : <p className="mt-3 text-sm text-slate-500">No requirements or licenses were published for this project.</p>}
      {project.requirements.length + project.licenses.length > 0 && <p className="mt-3 text-xs text-slate-500">The client reviews these qualifications after bids arrive.</p>}
    </section>

    <section className="mt-2 bg-white px-4 py-6">
      <h2 className="text-lg font-extrabold text-navy">Public Q&amp;A</h2>
      <p className="mt-1 text-sm text-slate-500">Ask a question about the project before submitting your bid.</p>
      {questions.length > 0 ? <div className="mt-3 divide-y divide-slate-200">{questions.map((question) => <article key={question.id} className="py-3"><strong className="text-xs text-navy">{question.senderUserId === currentUserId ? "You" : question.senderName}</strong><time className="ml-2 text-[10px] text-slate-500" dateTime={question.createdAt}>{new Date(question.createdAt).toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short" })}</time><p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-[#49627a]">{question.message}</p></article>)}</div> : <p className="mt-3 text-sm text-slate-500">No questions yet.</p>}
      <div className="mt-2">{awarded ? questionClosedNotice : questionForm}</div>
    </section>

    {canEditBid && !actionIsVisible && <MobileActionBar>
      <button type="button" onClick={onOpenBid} className="btn-red min-w-0 flex-1"><Lock className="h-4 w-4" />{myBid ? "Revise your Bid" : t("activity.submitBid")}</button>
      {canMessage && <button type="button" onClick={onMessage} aria-label="Message project client" title="Message project client" className="btn-outline w-11 shrink-0 p-0"><MessageCircle className="h-5 w-5" aria-hidden="true" /></button>}
    </MobileActionBar>}
  </div>;
}

function bidStatusNotice(awarded: boolean, biddingClosed: boolean, canEditBid: boolean) {
  if (awarded) return <p className="mt-3 text-xs text-slate-500">This project has been awarded.</p>;
  if (biddingClosed && !canEditBid) return <p className="mt-3 text-xs text-slate-500">Bidding has closed. The client is reviewing submitted bids.</p>;
  return null;
}
